import { Box, Button, Typography } from "@mui/material";

export type BulkActionItem = {
	label: string;
	onClick: () => void;
	color?: "error" | "primary" | "secondary" | "warning" | "info" | "success" | "inherit";
	loading?: boolean;
	disabled?: boolean;
};

type BulkActionBarProps = {
	selectedCount: number;
	/** Singular label for the entity, e.g. "booking", "personnel". */
	entityLabel: string;
	/** Plural label. Defaults to entityLabel + "s". */
	entityLabelPlural?: string;
	onClear: () => void;
	actions: BulkActionItem[];
};

export default function BulkActionBar({
	selectedCount,
	entityLabel,
	entityLabelPlural,
	onClear,
	actions
}: BulkActionBarProps) {
	if (selectedCount === 0) {
		return null;
	}

	const pluralLabel = entityLabelPlural ?? `${entityLabel}s`;
	const label = selectedCount === 1 ? entityLabel : pluralLabel;
	const isAnyLoading = actions.some((a) => a.loading);

	return (
		<Box
			sx={{
				position: "absolute",
				bottom: { xs: 16, lg: 30 },
				left: "50%",
				transform: "translateX(-50%)",
				backgroundColor: "text.primary",
				color: "common.white",
				px: { xs: 2, sm: 3 },
				py: { xs: 2, sm: 2 },
				borderRadius: 1,
				display: "flex",
				flexDirection: { xs: "column", sm: "row" },
				alignItems: { xs: "stretch", sm: "center" },
				gap: { xs: 2, sm: 3 },
				boxShadow: 4,
				zIndex: 40,
				width: { xs: "calc(100% - 32px)", sm: "max-content" }
			}}
		>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
				<Typography variant="text14" sx={{ fontWeight: 400 }}>
					{selectedCount} {label} selected
				</Typography>
				<Typography
					variant="text14"
					onClick={isAnyLoading ? undefined : onClear}
					sx={{
						textDecoration: "underline",
						cursor: isAnyLoading ? "default" : "pointer",
						fontWeight: 600,
						color: isAnyLoading ? "grey.500" : "common.white",
						"&:hover": { color: isAnyLoading ? "grey.500" : "grey.300" }
					}}
				>
					Clear selection
				</Typography>
			</Box>

			{actions.length > 0 && (
				<Box sx={{ display: "flex", gap: 1.5, ml: { sm: 2 } }}>
					{actions.map((action) => (
						<Button
							key={action.label}
							variant="outlined"
							color={action.color ?? "primary"}
							loading={action.loading}
							disabled={action.disabled}
							sx={{
								flex: { xs: 1, sm: "none" },
								backgroundColor: "common.white",
								textTransform: "none",
								fontWeight: 600,
								borderRadius: 1,
								px: 2.125,
								py: 1.125,
								lineHeight: 1,
								"&:hover": { backgroundColor: "grey.100" }
							}}
							onClick={action.onClick}
						>
							{action.label}
						</Button>
					))}
				</Box>
			)}
		</Box>
	);
}
